import React, { useCallback } from 'react'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { useJoinFunnelAnalytics } from '@components/Analytics/useJoinFunnelAnalytics'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { mapToErrorMessage } from '@components/Web3/utils'
import { useEmbeddedWallet } from '@towns/privy'
import { Analytics } from 'hooks/useAnalytics'
import { useDevice } from 'hooks/useDevice'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useIsSmartAccountAlreadyLinked, useLinkEOAOnSignUp } from 'hooks/useLinkEOAOnSignUp'
import { allowanceQueryKey, getUSDCAllowance } from 'hooks/useUSDCAllowance'
import { useUSDCApprove } from 'hooks/useUSDCApprove'
import {
    blockchainQueryKeys,
    getMembershipPrice,
    queryClient,
    SpaceAddressFromSpaceId,
    StreamView,
    TSigner,
    useBaseReads,
    useBaseWrites,
    useMintThenJoin,
    useMyDefaultUsernames,
    usePrimaryWallet,
    useTownsClient,
    useTownsContext,
} from 'use-towns-client'
import { useShallow } from 'zustand/react/shallow'

import { publicPageLoginStore } from '../usePublicPageLoginFlow'

import { useInstallSubscriptionAfterJoin } from './useInstallSubscriptionAfterJoin'

type TClientSingleton = ReturnType<typeof useTownsClient>['clientSingleton']
type TSignerContext = ReturnType<typeof useTownsContext>['signerContext']

export function useJoinTown({ spaceId }: { spaceId: string | undefined }) {
    const { joiningTown, joinedTown } = useJoinFunnelAnalytics()
    const defaultUsername = useMyDefaultUsernames()?.at(0)
    const installSubscriptionAfterJoin = useInstallSubscriptionAfterJoin({ spaceId })
    const mintThenJoin = useMintThenJoin()
    const isSmartAccountAlreadyLinked = useIsSmartAccountAlreadyLinked()
    const { embeddedWallet } = useEmbeddedWallet()
    // the root key is privy's embedded wallet
    // root key = privy embedded wallet = userId in towns
    const rootKeyAddress = embeddedWallet?.address
    const { data: primaryWallet } = usePrimaryWallet({ rootWallet: rootKeyAddress })

    const { setJoinStatusStep, endPublicPageLoginFlow } = publicPageLoginStore(
        useShallow((s) => ({
            setJoinStatusStep: s.setJoinStatusStep,
            endPublicPageLoginFlow: s.endPublicPageLoginFlow,
        })),
    )

    const { userOpsInstance } = useBaseWrites()

    const linkEOAOnSignUp = useLinkEOAOnSignUp()
    const { isTouch } = useDevice()
    const { enableUsdc } = useFeatureFlags()

    const eoaApproveUsdc = useEoaApproveUsdc()

    return useCallback(
        async ({
            signer,
            clientSingleton,
            signerContext,
            source,
            analyticsData,
        }: {
            signer: TSigner
            clientSingleton: TClientSingleton
            signerContext: TSignerContext
            source: string
            analyticsData: {
                spaceName: string | undefined
            }
        }) => {
            if (!spaceId || !clientSingleton || !signer || !signerContext || !userOpsInstance) {
                const missingParams: string[] = []
                if (!spaceId) {
                    missingParams.push('spaceId')
                }
                if (!clientSingleton) {
                    missingParams.push('clientSingleton')
                }
                if (!signer) {
                    missingParams.push('signer')
                }
                if (!signerContext) {
                    missingParams.push('signerContext')
                }
                if (!userOpsInstance) {
                    missingParams.push('userOpsInstance')
                }

                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="There was an error joining the town."
                        subMessage={mapToErrorMessage({
                            error: new Error(
                                `[usePublicPageLoginFlow] missing params: ${missingParams.join(
                                    ', ',
                                )} - ${source}`,
                            ),
                            source,
                        })}
                    />
                ))
                const { gatedSpace, priceInWei, pricingModule, tokensGatedBy } =
                    getCachedSpaceDataForAnalytics()
                Analytics.getInstance().track('joined town failed', {
                    spaceId,
                    spaceName: analyticsData.spaceName,
                    gatedSpace,
                    pricingModule,
                    priceInWei,
                    tokensGatedBy,
                })
                endPublicPageLoginFlow()
                return
            }

            joiningTown({ spaceId, spaceName: analyticsData.spaceName })

            try {
                const { isLinked, smartAccountAddress, rootKeyAddress } =
                    await isSmartAccountAlreadyLinked()

                // Handle EOA linking for new users
                let streamView: StreamView | undefined
                // mobile is a pretty intesnse flow if you're a new user with EOA
                // 4 times to switch between app and wallet!!
                // 2x swtich/sign for privy, 1x switch/sign for linking EOA, 1x switch/sign for joining space
                // So for now we're just not gonna deal with EOA on mobile for new users
                if (!isLinked && !isTouch) {
                    const { signerToUse } = await linkEOAOnSignUp({
                        rootKeyAddress,
                        signer,
                        smartAccountAddress,
                        smartAccountAlreadyLinked: isLinked,
                    })

                    const signerToUseAddress = await signerToUse.getAddress()
                    const signerIsRootKey =
                        rootKeyAddress.toLowerCase() === signerToUseAddress.toLowerCase()

                    if (signerIsRootKey) {
                        console.warn(
                            '[useJoinTown] new user: signer is the root key but should have been EOA',
                            {
                                rootKeyAddress,
                            },
                        )
                    } else {
                        console.log(
                            '[useJoinTown] new user: signer is the EOA, joining space with EOA',
                            {
                                rootKeyAddress,
                                signerToUse: signerToUseAddress,
                            },
                        )
                    }

                    // EOAs have to approve USDC every time they join a space
                    if (!signerIsRootKey && enableUsdc) {
                        try {
                            await eoaApproveUsdc({ signer: signerToUse, spaceId })
                        } catch (error) {
                            console.error('[useJoinTown] new user: error approving USDC', error)

                            const errorMessage = mapToErrorMessage({
                                error: error as Error,
                                source: 'join space',
                            })

                            if (errorMessage === 'ACTION_REJECTED') {
                                return
                            }
                            popupToast(({ toast }) => (
                                <StandardToast.Error
                                    toast={toast}
                                    message="There was an error approving the USDC."
                                    subMessage={errorMessage}
                                />
                            ))
                            return
                        }
                    }

                    streamView = await mintThenJoin({
                        spaceId,
                        signer: signerToUse,
                        signerContext,
                        // if signerIsRootKey we should always be using 'smartAccount' for the wallet type
                        walletType: signerIsRootKey ? 'smartAccount' : 'eoa',
                        onJoinFlowStatus: (status) => setJoinStatusStep(status),
                        defaultUsername,
                    })
                } else {
                    const signerIsRootKey =
                        rootKeyAddress.toLowerCase() === (await signer.getAddress()).toLowerCase()
                    const walletType = primaryWallet?.type || 'smartAccount'

                    console.log('[useJoinTown] existing user debug: ', {
                        signerIsRootKey,
                        walletType,
                        isTouch,
                        signerAddress: await signer.getAddress(),
                        rootKeyAddress,
                        smartAccountAddress,
                        isLinked,
                    })

                    if (signerIsRootKey && walletType === 'eoa' && !isTouch) {
                        console.warn(
                            '[useJoinTown] existing user: signer is the root key but should have been EOA',
                            {
                                rootKeyAddress,
                                signerAddress: await signer.getAddress(),
                            },
                        )
                    }

                    // EOAs have to approve USDC every time they join a space
                    if (!signerIsRootKey && enableUsdc) {
                        try {
                            await eoaApproveUsdc({ signer, spaceId })
                        } catch (error) {
                            endPublicPageLoginFlow()
                            console.error(
                                '[useJoinTown] existing user: error approving USDC',
                                error,
                            )
                            const errorMessage = mapToErrorMessage({
                                error: error as Error,
                                source: 'join space',
                            })

                            if (errorMessage === 'ACTION_REJECTED') {
                                return
                            }

                            popupToast(({ toast }) => (
                                <StandardToast.Error
                                    toast={toast}
                                    message="There was an error approving the USDC."
                                    subMessage={mapToErrorMessage({
                                        error: error as Error,
                                        source: 'join space',
                                    })}
                                />
                            ))
                            return
                        }
                    }

                    streamView = await mintThenJoin({
                        spaceId,
                        signer,
                        signerContext,
                        // root key signer, let's just always use smart account to prevent errors or stopping the flow
                        // this shouldn't happen but if it does, let the user keep going - the log above would indicate this
                        walletType: signerIsRootKey ? 'smartAccount' : walletType,
                        onJoinFlowStatus: (status) => setJoinStatusStep(status),
                        defaultUsername,
                    })
                }

                if (streamView) {
                    const { gatedSpace, priceInWei, pricingModule, tokensGatedBy, duration } =
                        getCachedSpaceDataForAnalytics()
                    joinedTown({
                        spaceId,
                        spaceName: analyticsData.spaceName,
                        gatedSpace,
                        pricingModule,
                        priceInWei,
                        tokensGatedBy,
                        duration,
                    })

                    // non blocking refetch of linked wallets
                    void queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.linkedWallets(rootKeyAddress),
                    })
                    void queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.primaryWallet(rootKeyAddress),
                    })

                    installSubscriptionAfterJoin({ userId: rootKeyAddress, signer })
                } else {
                    endPublicPageLoginFlow()
                    popupToast(({ toast }) => (
                        <StandardToast.Error
                            toast={toast}
                            message="There was an error joining the town."
                            subMessage={mapToErrorMessage({
                                error: new Error('unable to join space unknown'),
                                source: 'join space',
                            })}
                        />
                    ))
                    const { gatedSpace, priceInWei, pricingModule, tokensGatedBy } =
                        getCachedSpaceDataForAnalytics()
                    Analytics.getInstance().track('joined town failed', {
                        spaceId,
                        spaceName: analyticsData.spaceName,
                        gatedSpace,
                        pricingModule,
                        priceInWei,
                        tokensGatedBy,
                    })
                }
            } catch (error) {
                endPublicPageLoginFlow()
                const _error = error as Error

                const errorMessage = mapToErrorMessage({ error: _error, source: 'join space' })

                if (errorMessage === 'ACTION_REJECTED') {
                    return
                }

                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="There was an error joining the town."
                        subMessage={errorMessage}
                    />
                ))
            }
        },
        [
            spaceId,
            userOpsInstance,
            joiningTown,
            endPublicPageLoginFlow,
            isSmartAccountAlreadyLinked,
            isTouch,
            linkEOAOnSignUp,
            enableUsdc,
            mintThenJoin,
            defaultUsername,
            eoaApproveUsdc,
            setJoinStatusStep,
            primaryWallet?.type,
            joinedTown,
            installSubscriptionAfterJoin,
        ],
    )
}

function useEoaApproveUsdc() {
    const { mutateAsync: approveUSDC } = useUSDCApprove()
    const { userOpsInstance } = useBaseWrites()
    const { address: usdcTokenAddress } = useUSDCMetadata()
    const { spaceDapp } = useBaseReads()
    return useCallback(
        async ({ signer, spaceId }: { signer: TSigner; spaceId: string }) => {
            const signerAddress = await signer.getAddress()
            const spaceAddress = SpaceAddressFromSpaceId(spaceId)
            const qk = allowanceQueryKey(
                signerAddress as `0x${string}`,
                spaceAddress,
                usdcTokenAddress,
            )
            const allowance = await getUSDCAllowance({
                userOpsInstance,
                walletAddress: signerAddress as `0x${string}`,
                spenderAddress: SpaceAddressFromSpaceId(spaceId),
                usdcAddress: usdcTokenAddress,
            })
            queryClient.setQueryData(qk, allowance)

            const membershipPrice = await getMembershipPrice({
                spaceId,
                spaceDapp,
            })

            if (membershipPrice.toBigInt() > (allowance ?? 0n)) {
                await approveUSDC({
                    signer,
                    spenderAddress: spaceAddress,
                    amount: membershipPrice.toBigInt(),
                })
            }
        },
        [approveUSDC, spaceDapp, usdcTokenAddress, userOpsInstance],
    )
}
